import { useMemo } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Cell,
} from "recharts";

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-surface border border-line rounded-sm p-3 text-xs shadow-lg">
      <p className="font-bold text-white mb-1">
        {d.home_team || "?"} vs {d.away_team || "?"}
      </p>
      <p className="text-txt-secondary">
        {d.market} @ {d.odd} | Edge: {d.edge}%
      </p>
      <p className={`font-mono font-bold mt-1 ${d.result === "won" ? "text-volt" : d.result === "lost" ? "text-danger" : "text-warning"}`}>
        {d.result === "won" ? `+${d.profit} EUR` : d.result === "lost" ? `${d.profit} EUR` : "Pendente"}
      </p>
      <p className="text-txt-secondary mt-1">
        ROI acumulado: <span className="font-mono font-bold text-white">{d.cumulative_roi}%</span>
      </p>
    </div>
  );
}

function ProfitTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-surface border border-line rounded-sm p-3 text-xs shadow-lg">
      <p className="font-bold text-white">{d.label}</p>
      <p className="font-mono">{d.profit >= 0 ? "+" : ""}{d.profit} EUR</p>
    </div>
  );
}

export default function RoiCharts({ bets, summary }) {
  const chartData = useMemo(() => {
    if (!bets || bets.length === 0) return [];
    return bets.map((b, i) => ({
      ...b,
      index: i + 1,
      displayDate: b.created_at ? new Date(b.created_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "short" }) : "",
    }));
  }, [bets]);

  const profitByMarket = useMemo(() => {
    if (!bets || bets.length === 0) return [];
    const markets = {};
    bets.forEach((b) => {
      if (!markets[b.market]) markets[b.market] = { market: b.market, profit: 0, count: 0, wins: 0 };
      markets[b.market].profit += b.profit;
      markets[b.market].count += 1;
      if (b.result === "won") markets[b.market].wins += 1;
    });
    return Object.values(markets).map((m) => ({
      label: m.market,
      profit: Math.round(m.profit * 100) / 100,
      count: m.count,
      winRate: m.count > 0 ? Math.round((m.wins / m.count) * 100) : 0,
    }));
  }, [bets]);

  if (!bets || bets.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4" data-testid="roi-charts">
      {/* Cumulative Profit Chart */}
      <div className="bg-surface border border-line rounded-sm p-5">
        <h4 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-txt-secondary mb-4">
          Lucro Acumulado (Kelly)
        </h4>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00FF66" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#00FF66" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="displayDate"
                tick={{ fill: "#A1A1AA", fontSize: 10 }}
                axisLine={{ stroke: "#27272A" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "JetBrains Mono" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v}EUR`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="cumulative_profit"
                stroke="#00FF66"
                strokeWidth={2}
                fill="url(#profitGrad)"
                dot={false}
                activeDot={{ r: 4, fill: "#00FF66", stroke: "#0A0A0A", strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Profit by Market */}
      {profitByMarket.length > 0 && (
        <div className="bg-surface border border-line rounded-sm p-5">
          <h4 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-txt-secondary mb-4">
            Lucro por Mercado
          </h4>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={profitByMarket} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: "#A1A1AA", fontSize: 10 }}
                  axisLine={{ stroke: "#27272A" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${v}EUR`}
                />
                <Tooltip content={<ProfitTooltip />} />
                <Bar dataKey="profit" radius={[3, 3, 0, 0]}>
                  {profitByMarket.map((entry, i) => (
                    <Cell key={i} fill={entry.profit >= 0 ? "#00FF66" : "#FF3B30"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Bet History Table */}
      <div className="bg-surface border border-line rounded-sm p-5">
        <h4 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-txt-secondary mb-3">
          Historico de Apostas
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="bet-history-table">
            <thead>
              <tr className="border-b border-line text-txt-secondary">
                <th className="text-left py-2 px-2 font-bold uppercase tracking-widest">Jogo</th>
                <th className="text-left py-2 px-2 font-bold uppercase tracking-widest">Mercado</th>
                <th className="text-right py-2 px-2 font-bold uppercase tracking-widest">Odd</th>
                <th className="text-right py-2 px-2 font-bold uppercase tracking-widest">Edge</th>
                <th className="text-right py-2 px-2 font-bold uppercase tracking-widest">Stake</th>
                <th className="text-right py-2 px-2 font-bold uppercase tracking-widest">Resultado</th>
                <th className="text-right py-2 px-2 font-bold uppercase tracking-widest">P/L</th>
              </tr>
            </thead>
            <tbody>
              {chartData.map((b) => (
                <tr key={b.id} className="border-b border-line/30 hover:bg-surface-hover transition-colors">
                  <td className="py-2 px-2 text-white">
                    <span className="text-txt-secondary mr-1">[{b.league_code}]</span>
                    {b.home_team} vs {b.away_team}
                  </td>
                  <td className="py-2 px-2 font-mono">{b.market}</td>
                  <td className="py-2 px-2 font-mono text-right">{b.odd}</td>
                  <td className="py-2 px-2 font-mono text-right text-azure">{b.edge}%</td>
                  <td className="py-2 px-2 font-mono text-right">{b.stake}EUR</td>
                  <td className="py-2 px-2 text-right">
                    <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase ${
                      b.result === "won" ? "bg-volt/20 text-volt" :
                      b.result === "lost" ? "bg-danger/20 text-danger" :
                      "bg-warning/20 text-warning"
                    }`}>
                      {b.result === "won" ? "Ganhou" : b.result === "lost" ? "Perdeu" : "Pend."}
                    </span>
                  </td>
                  <td className={`py-2 px-2 font-mono text-right font-bold ${
                    b.profit > 0 ? "text-volt" : b.profit < 0 ? "text-danger" : "text-txt-secondary"
                  }`}>
                    {b.profit > 0 ? "+" : ""}{b.profit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
